import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SignalRService, ConnectionStatus, SignalRMessage } from '../../services/signalr.service';
import { HubConnectionState } from '@microsoft/signalr';

@Component({
  selector: 'app-signalr-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './signalr-status.component.html',
  styleUrl: './signalr-status.component.css'
})
export class SignalRStatusComponent implements OnInit, OnDestroy {
  @Input() showDetails = false;
  @Input() showMessageLog = false;
  @Input() maxMessages = 50;
  @Input() autoConnect = true;

  private destroy$ = new Subject<void>();

  connectionStatus: ConnectionStatus | null = null;
  recentMessages: SignalRMessage[] = [];
  messageCount = 0;
  isExpanded = false;

  constructor(private signalRService: SignalRService) {}

  ngOnInit(): void {
    // Subscribe to connection status
    this.signalRService.connectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.connectionStatus = status;
      });

    // Subscribe to messages if message log is enabled
    if (this.showMessageLog) {
      this.signalRService.messages$
        .pipe(takeUntil(this.destroy$))
        .subscribe(message => {
          this.addMessage(message);
        });
    }

    // Auto-connect if enabled
    if (this.autoConnect && !this.signalRService.isConnected) {
      this.connect();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addMessage(message: SignalRMessage): void {
    this.recentMessages.unshift(message);
    this.messageCount++;
    
    // Limit message history
    if (this.recentMessages.length > this.maxMessages) {
      this.recentMessages = this.recentMessages.slice(0, this.maxMessages);
    }
  }

  connect(): void {
    this.signalRService.connect().catch(error => {
      console.error('Failed to connect:', error);
    });
  }

  disconnect(): void {
    this.signalRService.disconnect().catch(error => {
      console.error('Failed to disconnect:', error);
    });
  }

  forceReconnect(): void {
    this.signalRService.forceReconnect();
  }

  clearMessages(): void {
    this.recentMessages = [];
    this.messageCount = 0;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  // Helper methods for template
  getConnectionStateIcon(): string {
    if (!this.connectionStatus) return 'fas fa-question-circle';
    
    switch (this.connectionStatus.state) {
      case HubConnectionState.Connected:
        return 'fas fa-wifi text-success';
      case HubConnectionState.Connecting:
      case HubConnectionState.Reconnecting:
        return 'fas fa-spinner fa-spin text-warning';
      case HubConnectionState.Disconnected:
        return 'fas fa-wifi-slash text-danger';
      case HubConnectionState.Disconnecting:
        return 'fas fa-spinner fa-spin text-muted';
      default:
        return 'fas fa-question-circle text-muted';
    }
  }

  getConnectionStateText(): string {
    if (!this.connectionStatus) return 'Unknown';
    
    switch (this.connectionStatus.state) {
      case HubConnectionState.Connected:
        return 'Connected';
      case HubConnectionState.Connecting:
        return 'Connecting...';
      case HubConnectionState.Reconnecting:
        return 'Reconnecting...';
      case HubConnectionState.Disconnected:
        return 'Disconnected';
      case HubConnectionState.Disconnecting:
        return 'Disconnecting...';
      default:
        return 'Unknown';
    }
  }

  getConnectionStateClass(): string {
    if (!this.connectionStatus) return 'text-muted';
    
    switch (this.connectionStatus.state) {
      case HubConnectionState.Connected:
        return 'text-success';
      case HubConnectionState.Connecting:
      case HubConnectionState.Reconnecting:
        return 'text-warning';
      case HubConnectionState.Disconnected:
        return 'text-danger';
      case HubConnectionState.Disconnecting:
        return 'text-muted';
      default:
        return 'text-muted';
    }
  }

  getMessageTypeIcon(messageType: string): string {
    switch (messageType.toLowerCase()) {
      case 'notification':
        return 'fas fa-bell text-info';
      case 'orderupdate':
        return 'fas fa-box text-primary';
      case 'systemalert':
        return 'fas fa-exclamation-triangle text-warning';
      case 'prealertupdate':
        return 'fas fa-ship text-info';
      case 'documentupdate':
        return 'fas fa-file-alt text-secondary';
      default:
        return 'fas fa-envelope text-muted';
    }
  }

  getMessageTypeClass(messageType: string): string {
    switch (messageType.toLowerCase()) {
      case 'notification':
        return 'border-info';
      case 'orderupdate':
        return 'border-primary';
      case 'systemalert':
        return 'border-warning';
      case 'prealertupdate':
        return 'border-info';
      case 'documentupdate':
        return 'border-secondary';
      default:
        return 'border-muted';
    }
  }

  formatTimestamp(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  }

  formatDuration(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  }

  copyConnectionId(): void {
    if (this.connectionStatus?.connectionId) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.connectionStatus.connectionId).then(() => {
          console.log('Connection ID copied to clipboard');
        }).catch(err => {
          console.error('Failed to copy connection ID:', err);
        });
      }
    }
  }

  exportMessages(): void {
    if (this.recentMessages.length === 0) {
      alert('No messages to export');
      return;
    }
    
    const csvContent = this.generateMessagesCSV();
    this.downloadCSV(csvContent, 'signalr-messages.csv');
  }

  private generateMessagesCSV(): string {
    const headers = ['Timestamp', 'Type', 'Data', 'User', 'Connection ID'];
    const rows = this.recentMessages.map(message => [
      message.timestamp.toISOString(),
      message.type,
      JSON.stringify(message.data).replace(/"/g, '""'),
      message.user || '',
      message.connectionId || ''
    ]);
    
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  private downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  // Public methods for testing
  sendTestMessage(): void {
    this.signalRService.sendNotification('test', {
      message: 'Test message from SignalR status component',
      timestamp: new Date().toISOString()
    }).catch(error => {
      console.error('Failed to send test message:', error);
    });
  }

  sendTestOrderUpdate(): void {
    this.signalRService.sendOrderUpdate({
      orderId: 'TEST-' + Date.now(),
      status: 'Updated',
      timestamp: new Date().toISOString()
    }).catch(error => {
      console.error('Failed to send test order update:', error);
    });
  }

  sendTestSystemAlert(): void {
    this.signalRService.sendSystemAlert({
      level: 'info',
      message: 'Test system alert',
      timestamp: new Date().toISOString()
    }).catch(error => {
      console.error('Failed to send test system alert:', error);
    });
  }
}